<template>
  <b-container fluid>
    <b-row align-v="end">
      <b-col
        cols="12"
        lg="6"
        xl="4"
      >
        <b-form-group>
          <label> {{ $t('fields.general.type')}} </label>
          <s-input-label-edit
            v-model="circuitBreakerInfo.type"
            :editable="editMode"
            :formattedValue="getLabel(typeOptions, circuitBreakerInfo.type)"
          >
            <s-select
              v-model="circuitBreakerInfo.type"
              :options="typeOptions"
              :disabled="saving"
            />
          </s-input-label-edit>
        </b-form-group>
      </b-col>
      <b-col
        cols="12"
        lg="6"
        xl="4"
      >
        <b-form-group>
          <label> {{ $t('fields.asset.circuit-breaker.installation-place')}} </label>
          <s-input-label-edit
            v-model="circuitBreakerInfo.installationPlace"
            :editable="editMode"
            :formattedValue="getLabel(
              installationPlaces,
              circuitBreakerInfo.installationPlace
            )"
          >
            <s-select
              v-model="circuitBreakerInfo.installationPlace"
              :options="installationPlaces"
              :disabled="saving"
            />
          </s-input-label-edit>
        </b-form-group>
      </b-col>
      <b-col
        cols="12"
        lg="6"
        xl="4"
      >
        <b-form-group>
          <label>{{ $t('fields.asset.circuit-breaker.electric-arch-extinction-methods')}}</label>
          <s-input-label-edit
            v-model="circuitBreakerInfo.electricArchExtinctionMethod"
            :editable="editMode"
            :formattedValue="getLabel(
              electricArchExtinctionMethods,
              circuitBreakerInfo.electricArchExtinctionMethod,
            )"
          >
            <s-select
              v-model="circuitBreakerInfo.electricArchExtinctionMethod"
              :options="electricArchExtinctionMethods"
              :disabled="saving"
            />
          </s-input-label-edit>
        </b-form-group>
      </b-col>
      <b-col
        cols="12"
        lg="6"
        xl="4"
      >
        <b-form-group>
          <label>{{ $t('fields.asset.circuit-breaker.nominal-tension') }}</label>
          <s-input-label-edit
            v-model="circuitBreakerInfo.nominalTension"
            :editable="editMode"
            :formatted-value="formatNumberValue(
              circuitBreakerInfo.nominalTension,
              fieldsDefinitions.nominalTension.precision,
            )"
          >
            <s-number-input
              class="text-right"
              v-model="circuitBreakerInfo.nominalTension"
              :disabled="saving"
              :validation="$v.circuitBreakerInfo.nominalTension"
              :invalid="$v.circuitBreakerInfo.nominalTension.$invalid"
              :numeric-options="configureNumericOptionsPrecision(
                fieldsDefinitions.nominalTension
              )"
            />
            <b-form-invalid-feedback>
              {{ getRangeValueMessage(fieldsDefinitions.nominalTension) }}
            </b-form-invalid-feedback>
          </s-input-label-edit>
        </b-form-group>
      </b-col>
      <b-col
        cols="12"
        lg="6"
        xl="4"
      >
        <b-form-group>
          <label>{{ $t('fields.asset.circuit-breaker.nominal-chain') }}</label>
          <s-input-label-edit
            v-model="circuitBreakerInfo.nominalChain"
            :editable="editMode"
            :formatted-value="formatNumberValue(
              circuitBreakerInfo.nominalChain,
              fieldsDefinitions.nominalChain.precision,
            )"
          >
            <s-number-input
              class="text-right"
              v-model="circuitBreakerInfo.nominalChain"
              :disabled="saving"
              :validation="$v.circuitBreakerInfo.nominalChain"
              :invalid="$v.circuitBreakerInfo.nominalChain.$invalid"
              :numeric-options="configureNumericOptionsPrecision(
                fieldsDefinitions.nominalChain
              )"
            />
            <b-form-invalid-feedback>
              {{ getRangeValueMessage(fieldsDefinitions.nominalChain) }}
            </b-form-invalid-feedback>
          </s-input-label-edit>
        </b-form-group>
      </b-col>
      <b-col
        cols="12"
        lg="6"
        xl="4"
      >
        <b-form-group>
          <label>{{ $t('fields.asset.circuit-breaker.nominal-frequency') }}</label>
          <s-input-label-edit
            v-model="circuitBreakerInfo.nominalFrequency"
            :editable="editMode"
            :formatted-value="formatNumberValue(
              circuitBreakerInfo.nominalFrequency,
              fieldsDefinitions.nominalFrequency.precision,
            )"
          >
            <s-number-input
              class="text-right"
              v-model="circuitBreakerInfo.nominalFrequency"
              :disabled="saving"
              :validation="$v.circuitBreakerInfo.nominalFrequency"
              :invalid="$v.circuitBreakerInfo.nominalFrequency.$invalid"
              :numeric-options="configureNumericOptionsPrecision(
                fieldsDefinitions.nominalFrequency
              )"
            />
            <b-form-invalid-feedback>
              {{ getRangeValueMessage(fieldsDefinitions.nominalFrequency) }}
            </b-form-invalid-feedback>
          </s-input-label-edit>
        </b-form-group>
      </b-col>
      <b-col
        cols="12"
        lg="6"
        xl="4"
      >
        <b-form-group>
          <label>{{ $t('fields.asset.circuit-breaker.opening-time') }}</label>
          <s-input-label-edit
            v-model="circuitBreakerInfo.typicalOpeningTime"
            :editable="editMode"
            :formatted-value="formatNumberValue(
              circuitBreakerInfo.typicalOpeningTime,
              fieldsDefinitions.typicalOpeningTime.precision,
            )"
          >
            <s-number-input
              v-model="circuitBreakerInfo.typicalOpeningTime"
              class="text-right"
              :disabled="saving"
              :validation="$v.circuitBreakerInfo.typicalOpeningTime"
              :invalid="$v.circuitBreakerInfo.typicalOpeningTime.$invalid"
              :numeric-options="configureNumericOptionsPrecision(
                fieldsDefinitions.typicalOpeningTime
              )"
            />
            <b-form-invalid-feedback>
              {{ getRangeValueMessage(fieldsDefinitions.typicalOpeningTime) }}
            </b-form-invalid-feedback>
          </s-input-label-edit>
        </b-form-group>
      </b-col>
      <b-col
        cols="12"
        lg="6"
        xl="4"
      >
        <b-form-group>
          <label>{{ $t('fields.asset.circuit-breaker.closing-time') }}</label>
          <s-input-label-edit
            v-model="circuitBreakerInfo.maximumClosingTime"
            :editable="editMode"
            :formatted-value="formatNumberValue(
              circuitBreakerInfo.maximumClosingTime,
              fieldsDefinitions.maximumClosingTime.precision,
            )"
          >
            <s-number-input
              class="text-right"
              v-model="circuitBreakerInfo.maximumClosingTime"
              :disabled="saving"
              :validation="$v.circuitBreakerInfo.maximumClosingTime"
              :invalid="$v.circuitBreakerInfo.maximumClosingTime.$invalid"
              :numeric-options="configureNumericOptionsPrecision(
                fieldsDefinitions.maximumClosingTime
              )"
            />
            <b-form-invalid-feedback>
              {{ getRangeValueMessage(fieldsDefinitions.maximumClosingTime) }}
            </b-form-invalid-feedback>
          </s-input-label-edit>
        </b-form-group>
      </b-col>
      <b-col
        cols="12"
        lg="6"
        xl="4"
      >
        <b-form-group>
          <label>{{ $t('fields.asset.circuit-breaker.nominal-sequence-operation') }}</label>
          <s-input-label-edit
            v-model="circuitBreakerInfo.nominalOperationSequence"
            :editable="editMode"
            :input-options="inputOptions.nominalOperationSequence"
          />
        </b-form-group>
      </b-col>
      <b-col
        cols="12"
        xl="8"
      >
        <b-container
          fluid
          class="border rounded mb-1 py-3"
        >
          <b-row class="mb-3">
            <b-col>
              <label>{{ $t('fields.asset.circuit-breaker.interruption-capability') }}</label>
            </b-col>
          </b-row>
          <b-row
            align-v="end"
            class="mt-xl-5"
          >
            <b-col
              cols="12"
              lg="6"
            >
              <b-form-group>
                <label>{{ $t('fields.asset.circuit-breaker.nominal') }}</label>
                <s-input-label-edit
                  v-model="circuitBreakerInfo.interruptionCapacity.nominal"
                  :editable="editMode"
                  :formatted-value="formatNumberValue(
                    circuitBreakerInfo.interruptionCapacity.nominal,
                    fieldsDefinitions.interruptionCapacity.nominal.precision,
                  )"
                >
                  <s-number-input
                    class="text-right"
                    v-model="circuitBreakerInfo.interruptionCapacity.nominal"
                    :disabled="saving"
                    :validation="$v.circuitBreakerInfo.interruptionCapacity.nominal"
                    :invalid="$v.circuitBreakerInfo.interruptionCapacity.nominal.$invalid"
                    :numeric-options="configureNumericOptionsPrecision(
                      fieldsDefinitions.interruptionCapacity
                      .nominal
                    )"
                  />
                  <b-form-invalid-feedback>
                    {{ getRangeValueMessage(fieldsDefinitions.interruptionCapacity.nominal) }}
                  </b-form-invalid-feedback>
                </s-input-label-edit>
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              lg="6"
            >
              <b-form-group>
                <label>{{ $t('fields.asset.circuit-breaker.continuous-chain') }}</label>
                <s-input-label-edit
                  v-model="circuitBreakerInfo.interruptionCapacity.continuousChainPercentage"
                  :editable="editMode"
                  :formatted-value="formatNumberValue(
                    circuitBreakerInfo.interruptionCapacity.continuousChainPercentage,
                    fieldsDefinitions.interruptionCapacity.continuousChainPercentage.precision,
                  )"
                >
                  <s-number-input
                    class="text-right"
                    v-model="circuitBreakerInfo.interruptionCapacity.continuousChainPercentage"
                    :disabled="saving"
                    :validation="$v.circuitBreakerInfo.interruptionCapacity
                      .continuousChainPercentage"
                    :invalid="$v.circuitBreakerInfo.interruptionCapacity
                      .continuousChainPercentage.$invalid"
                    :numeric-options="configureNumericOptionsPrecision(
                      fieldsDefinitions
                      .interruptionCapacity
                      .continuousChainPercentage
                    )"
                  />
                  <b-form-invalid-feedback>
                    {{
                      getRangeValueMessage(
                        fieldsDefinitions.interruptionCapacity.continuousChainPercentage
                      )
                    }}
                  </b-form-invalid-feedback>
                </s-input-label-edit>
              </b-form-group>
            </b-col>
          </b-row>
        </b-container>
      </b-col>
      <b-col align-self="start"
        cols="12"
        lg="6"
        xl="4"
      >
        <b-form-group>
          <label>{{ $t('fields.asset.circuit-breaker.operation-mechanism') }}</label>
          <s-input-label-edit
            class="mb-xl-3"
            v-model="circuitBreakerInfo.mechanismOperation"
            :editable="editMode"
            :formattedValue="getLabel(
              mechanismOperationTypes,
              circuitBreakerInfo.mechanismOperation
            )"
          >
            <s-select
              v-model="circuitBreakerInfo.mechanismOperation"
              :options="mechanismOperationTypes"
              :disabled="saving"
            />
          </s-input-label-edit>
        </b-form-group>
        <b-form-group>
          <label>{{ $t('fields.asset.circuit-breaker.short-duration-chain') }}</label>
          <s-input-label-edit
            v-model="circuitBreakerInfo.supportableShortChainDuration"
            :editable="editMode"
            :input-options="inputOptions.supportableShortChainDuration"
          />
        </b-form-group>
      </b-col>
      <b-col cols="12">
        <b-container
          fluid
          class="border rounded my-3 py-3 h-100"
        >
          <b-row class="mb-3">
            <b-col>
              <label>{{ $t('fields.asset.circuit-breaker.supportable-nominal-chain') }}</label>
            </b-col>
          </b-row>
          <b-row
            align-v="end"
            class="mt-xl-5"
          >
            <b-col
              cols="12"
              lg="6"
              xl="4"
            >
              <b-form-group>
                <label>{{ $t('fields.asset.circuit-breaker.applied-voltage') }}</label>
                <s-input-label-edit
                  v-model="circuitBreakerInfo.supportableNominalTension.applied"
                  :editable="editMode"
                  :formatted-value="formatNumberValue(
                    circuitBreakerInfo.supportableNominalTension.applied,
                    fieldsDefinitions.supportableNominalTension.applied.precision,
                  )"
                >
                  <s-number-input
                    v-model="circuitBreakerInfo.supportableNominalTension.applied"
                    class="text-right"
                    :disabled="saving"
                    :validation="$v.circuitBreakerInfo.supportableNominalTension.applied"
                    :invalid="$v.circuitBreakerInfo.supportableNominalTension.applied.$invalid"
                    :numeric-options="configureNumericOptionsPrecision(
                      fieldsDefinitions
                      .supportableNominalTension
                      .applied
                    )"
                  />
                  <b-form-invalid-feedback>
                    {{ getRangeValueMessage(fieldsDefinitions.supportableNominalTension.applied) }}
                  </b-form-invalid-feedback>
                </s-input-label-edit>
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              lg="6"
              xl="4"
            >
              <b-form-group>
                <label>{{ $t('fields.asset.circuit-breaker.atmospheric-impulse') }}</label>
                <s-input-label-edit
                  v-model="circuitBreakerInfo.supportableNominalTension.atmosphericImpulse"
                  :editable="editMode"
                  :formatted-value="formatNumberValue(
                    circuitBreakerInfo.supportableNominalTension.atmosphericImpulse,
                    fieldsDefinitions.supportableNominalTension.atmosphericImpulse.precision,
                  )"
                >
                  <s-number-input
                    v-model="circuitBreakerInfo.supportableNominalTension.atmosphericImpulse"
                    class="text-right"
                    :disabled="saving"
                    :validation="$v.circuitBreakerInfo.supportableNominalTension.atmosphericImpulse"
                    :invalid="$v.circuitBreakerInfo
                      .supportableNominalTension
                      .atmosphericImpulse
                      .$invalid"
                    :numeric-options="configureNumericOptionsPrecision(
                      fieldsDefinitions
                      .supportableNominalTension
                      .atmosphericImpulse
                    )"
                  />
                  <b-form-invalid-feedback>
                    {{ getRangeValueMessage(
                        fieldsDefinitions.supportableNominalTension.atmosphericImpulse
                      )
                    }}
                  </b-form-invalid-feedback>
                </s-input-label-edit>
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              lg="6"
              xl="4"
            >
              <b-form-group>
                <label>{{ $t('fields.asset.circuit-breaker.industrial-frequency') }}</label>
                <s-input-label-edit
                  v-model="circuitBreakerInfo.supportableNominalTension.industrialFrequency"
                  :editable="editMode"
                  :formatted-value="formatNumberValue(
                    circuitBreakerInfo.supportableNominalTension.industrialFrequency,
                    fieldsDefinitions.supportableNominalTension.industrialFrequency.precision,
                  )"
                >
                  <s-number-input
                    v-model="circuitBreakerInfo.supportableNominalTension.industrialFrequency"
                    class="text-right"
                    :disabled="saving"
                    :validation="$v.circuitBreakerInfo
                      .supportableNominalTension
                      .industrialFrequency"
                    :invalid="$v.circuitBreakerInfo
                      .supportableNominalTension
                      .industrialFrequency
                      .$invalid"
                    :numeric-options="configureNumericOptionsPrecision(
                      fieldsDefinitions
                      .supportableNominalTension
                      .industrialFrequency
                    )"
                  />
                  <b-form-invalid-feedback>
                    {{ getRangeValueMessage(
                        fieldsDefinitions.supportableNominalTension.industrialFrequency
                      )
                    }}
                  </b-form-invalid-feedback>
                </s-input-label-edit>
              </b-form-group>
            </b-col>
          </b-row>
        </b-container>
      </b-col>
      <b-col cols="12">
        <b-form-group>
          <label>{{ $t('fields.asset.maintenance-history') }}</label>
          <s-input-label-edit
            v-model.trim="circuitBreakerInfo.maintenanceHistory"
            :editable="editMode"
            input-type="textarea"
            :input-options="textAreaOptions"
          />
        </b-form-group>
      </b-col>
      <b-col cols="12">
        <b-form-group>
          <label>{{ $t('fields.general.observations') }}</label>
          <s-input-label-edit
            v-model.trim="circuitBreakerInfo.observation"
            :editable="editMode"
            input-type="textarea"
            :input-options="textAreaOptions"
          />
        </b-form-group>
      </b-col>
    </b-row>
  </b-container>
</template>
<script>
import {
  DisconnectorCircuitBreakerType,
  MethodsOfExtinguishingElectricArc,
  OperationMechanism,
  InstallationPlace,
} from 'sigma-mappings';
import { mixinBuilder } from '@/services/validations/builder';
import fieldsDefinitions from '@/domain/data/installation/assetInformation/circuitBreakerFieldsDefinitions';

export default {
  name: 'CircuitBreakerForm',
  props: {
    editMode: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    circuitBreakerInfo: {
      type: Object,
      default: () => ({
        interruptionCapacity: {},
      }),
    },
  },
  mixins: [
    mixinBuilder({
      fieldsDefinitions,
      dataPath: 'circuitBreakerInfo',
    }),
  ],
  data: () => ({
    fieldsDefinitions,
  }),
  computed: {
    inputOptions() {
      return {
        supportableShortChainDuration: {
          maxlength: 25,
          formatter: this.invalidCharReplacer,
          disabled: this.saving,
        },
        nominalOperationSequence: {
          maxlength: 50,
          formatter: this.invalidCharReplacer,
          disabled: this.saving,
        },
        applied: {
          maxLength: fieldsDefinitions.supportableNominalTension.applied.maxLength,
          state: this.$bsStateValidator(
            this.$v.circuitBreakerInfo.supportableNominalTension.applied.$invalid,
          ),
        },
        nominalTension: {
          maxLength: fieldsDefinitions.nominalTension.maxLength,
          state: this.$bsStateValidator(this.$v.circuitBreakerInfo.nominalTension.$invalid),
        },
        nominalChain: {
          maxLength: fieldsDefinitions.nominalChain.maxLength,
          state: this.$bsStateValidator(this.$v.circuitBreakerInfo.nominalChain.$invalid),
        },
        nominalFrequency: {
          maxLength: fieldsDefinitions.nominalFrequency.maxLength,
          state: this.$bsStateValidator(this.$v.circuitBreakerInfo.nominalFrequency.$invalid),
        },
        typicalOpeningTime: {
          maxLength: fieldsDefinitions.typicalOpeningTime.maxLength,
          state: this.$bsStateValidator(this.$v.circuitBreakerInfo.typicalOpeningTime.$invalid),
        },
        maximumClosingTime: {
          maxLength: fieldsDefinitions.maximumClosingTime.maxLength,
          state: this.$bsStateValidator(this.$v.circuitBreakerInfo.maximumClosingTime.$invalid),
        },
        interruptionCapacityNominal: {
          maxLength: fieldsDefinitions.interruptionCapacity.nominal.maxLength,
          state: this.$bsStateValidator(
            this.$v.circuitBreakerInfo.interruptionCapacity.nominal.$invalid,
          ),
        },
        interruptionCapacityContinuousChainPercentage: {
          maxLength: fieldsDefinitions.interruptionCapacity.continuousChainPercentage.maxLength,
          state: this.$bsStateValidator(
            this.$v.circuitBreakerInfo.interruptionCapacity.continuousChainPercentage.$invalid,
          ),
        },
        industrialFrequency: {
          maxLength: fieldsDefinitions.supportableNominalTension.industrialFrequency.maxLength,
          state: this.$bsStateValidator(
            this.$v.circuitBreakerInfo.supportableNominalTension.industrialFrequency.$invalid,
          ),
        },
        atmosphericImpulse: {
          maxLength: fieldsDefinitions.supportableNominalTension.atmosphericImpulse.maxLength,
          state: this.$bsStateValidator(
            this.$v.circuitBreakerInfo.supportableNominalTension.atmosphericImpulse.$invalid,
          ),
        },
      };
    },
    nullCase() {
      return {
        text: this.$t('general.select'),
        value: null,
      };
    },
    typeOptions() {
      const basePath = 'enums.asset-info.polarity';
      return [
        this.nullCase,
        {
          value: DisconnectorCircuitBreakerType.MONOPOLAR,
          text: this.$t(`${basePath}.monopolar`),
        },
        {
          value: DisconnectorCircuitBreakerType.TRIPOLAR,
          text: this.$t(`${basePath}.tripolar`),
        },
      ];
    },
    installationPlaces() {
      const basePath = 'enums.asset-info.installation-place';
      return [
        this.nullCase,
        {
          value: InstallationPlace.EXTERNAL,
          text: this.$t(`${basePath}.external`),
        },
        {
          value: InstallationPlace.INTERNAL,
          text: this.$t(`${basePath}.internal`),
        },
      ];
    },
    mechanismOperationTypes() {
      const basePath = 'enums.asset-info.mechanism-operation-types';
      return [
        this.nullCase,
        {
          value: OperationMechanism.SPRING,
          text: this.$t(`${basePath}.spring`),
        },
        {
          value: OperationMechanism.PNEUMATIC,
          text: this.$t(`${basePath}.pneumatic`),
        },
        {
          value: OperationMechanism.HYDRAULIC,
          text: this.$t(`${basePath}.hydraulic`),
        },
      ];
    },
    electricArchExtinctionMethods() {
      const basePath = 'enums.asset-info.electric-arch-extinction-methods';
      return [
        this.nullCase,
        {
          value: MethodsOfExtinguishingElectricArc.GAS,
          text: this.$t(`${basePath}.gas`),
        },
        {
          value: MethodsOfExtinguishingElectricArc.VACUUM,
          text: this.$t(`${basePath}.vacuum`),
        },
        {
          value: MethodsOfExtinguishingElectricArc.MAGNETIC_BLOW,
          text: this.$t(`${basePath}.magnetic-blow`),
        },
        {
          value: MethodsOfExtinguishingElectricArc.COMPRESSED_AIR,
          text: this.$t(`${basePath}.compressed-air`),
        },
        {
          value: MethodsOfExtinguishingElectricArc.INSULATING_OIL,
          text: this.$t(`${basePath}.insulating-oil`),
        },
        {
          value: MethodsOfExtinguishingElectricArc.UNDEFINED,
          text: this.$t(`${basePath}.undefined`),
        },
      ];
    },
    textAreaOptions() {
      return {
        disabled: this.saving,
        maxLength: 2000,
      };
    },
    invalidData: {
      cache: false,
      get() {
        return this.$v.$invalid;
      },
    },
  },
  created() {
    this.updateFormStatus();
  },
  methods: {
    getLabel(options, label = null) {
      if (!label) {
        return '-';
      }
      const labelProxy = options.find(({ value }) => value === label);
      return labelProxy.text ? labelProxy.text : 'teste';
    },
    getRangeValueMessage(fieldDefinition) {
      const options = {
        minimumFractionDigits: fieldDefinition.precision,
        maximumFractionDigits: fieldDefinition.precision,
      };
      return this.$t('messages.MA086.message', {
        minValue: this.$n(fieldDefinition.min, options),
        maxValue: this.$n(fieldDefinition.max, options),
      });
    },
    configureNumericOptionsPrecision(field) {
      return {
        decimalPlaces: field.precision,
      };
    },
    formatNumberValue(value, decimais = 0) {
      if (typeof value !== 'number') {
        return value;
      }
      return this.$n(value, {
        minimumFractionDigits: decimais,
        maximumFractionDigits: decimais,
      });
    },
    invalidCharReplacer(value) {
      return value.replace(/([^\w\u00C0-\u017FZ\-,;/()_]+)/g, '');
    },
    updateFormStatus() {
      this.$emit('valid-data-change', !this.invalidData);
    },
  },
  watch: {
    invalidData() {
      this.updateFormStatus();
    },
  },
};
</script>
